import {
  trans,
  PHANTASIALAND_UI_ERRORS_REQUIRED,
  PHANTASIALAND_UI_ERRORS_FIELDSET_REQUIRED,
  PHANTASIALAND_UI_ERRORS_IS_EQUAL,
  PHANTASIALAND_UI_ERRORS_EMAIL,
  PHANTASIALAND_UI_ERRORS_MINLENGTH,
  PHANTASIALAND_UI_ERRORS_MAXLENGTH,
  PHANTASIALAND_UI_ERRORS_NUMBER,
  PHANTASIALAND_UI_ERRORS_MIN,
  PHANTASIALAND_UI_ERRORS_MAX,
  PHANTASIALAND_UI_ERRORS_NOT_OLD_ENOUGH,
  PHANTASIALAND_UI_ERRORS_PHONE,
  PHANTASIALAND_UI_ERRORS_TEXT_ONLY,
  PHANTASIALAND_UI_ERRORS_NOT_NUMERIC_ONLY
} from './translator'

import { create, test, enforce, only, each } from 'vest'
import { isEmail, isBefore } from 'validator'

enforce.extend({
  isEmail, isBefore
})

const formErrorClass = 'form__error'
const formErrorDataAttribute = 'data-js-form-error'
const formSelectWrapperClass = '.form__select-wrapper'
function init () {
  const forms = document.querySelectorAll('form[data-vest-validate]')
  forms.forEach((form) => {
    form.setAttribute('novalidate', 'novalidate')

    let formData = Object.fromEntries(new FormData(form).entries())
    const inputs = Array.from(form.querySelectorAll('input, textarea, select'))

    inputs.forEach((input) => {
      input.addEventListener('input', () => {
        formData = Object.fromEntries(new FormData(form).entries())

        const result = validationSuite(form)(formData, input.name)

        updateFieldErrorState(form, input, result)
      })
    })

    form.addEventListener('submit', (event) => {
      event.preventDefault()

      const errors = validateAndShowErrors(form)

      if (Object.keys(errors).length === 0) {
        form.submit()
      }
    })
  })
}

init()

document.addEventListener('initializeValidation', function () {
  init()
})

const validationSuite = (form) => create((data = {}, currentField) => {
  if (currentField) only(currentField)

  const inputs = Array.from(form.querySelectorAll('input, textarea, select'))

  each(inputs, (input) => {
    if (input.disabled) return
    const name = input.name
    const value = data[name]
    const label = getFieldLabel(input)
    // Ein ganzer Satz als Label (Checkbox) ergibt keine lesbare „<Feld> ist ein Pflichtfeld“-Meldung.
    const requiredMessage = input.getAttribute('data-vest-required-message') ||
      trans(PHANTASIALAND_UI_ERRORS_REQUIRED, { '%field%': label }, 'validators')

    if (input.hasAttribute('required')) {
      test(name, requiredMessage, () => {
        enforce(value).isNotEmpty()
      })

      if (input.type === 'number') {
        test(name, trans(PHANTASIALAND_UI_ERRORS_NUMBER, { '%field%': label }, 'validators'), () => {
          enforce(Number(value)).isNumber()
        })
      } else {
        test(name, requiredMessage, () => {
          enforce(value).isNotEmpty()
        })
      }
    }

    if (input.hasAttribute('minlength')) {
      const min = input.getAttribute('minlength') || input.getAttribute('data-minlength')
      test(name, trans(PHANTASIALAND_UI_ERRORS_MINLENGTH, { '%field%': label, '%min%': min }, 'validators'), () => {
        enforce(value).longerThanOrEquals(Number(min))
      })
    }

    if (input.hasAttribute('maxlength')) {
      const max = input.getAttribute('maxlength') || input.getAttribute('data-maxlength')
      test(name, trans(PHANTASIALAND_UI_ERRORS_MAXLENGTH, { '%field%': label, '%max%': max }, 'validators'), () => {
        enforce(value).shorterThanOrEquals(Number(max))
      })
    }

    if (input.type === 'email' && value !== '') {
      test(name, trans(PHANTASIALAND_UI_ERRORS_EMAIL, {}, 'validators'), () => {
        enforce(value).isEmail()
      })
    }

    if (input.type === 'number' && input.hasAttribute('min')) {
      const min = input.getAttribute('min')
      test(name, trans(PHANTASIALAND_UI_ERRORS_MIN, { '%min%': min }, 'validators'), () => {
        enforce(Number(value)).greaterThanOrEquals(Number(min))
      })
    }

    if (input.type === 'number' && input.hasAttribute('max')) {
      const max = input.getAttribute('max')
      test(name, trans(PHANTASIALAND_UI_ERRORS_MAX, { '%max%': max }, 'validators'), () => {
        enforce(Number(value)).lessThanOrEquals(Number(max))
      })
    }

    if (input.type === 'tel' && value !== '') {
      test(name, trans(PHANTASIALAND_UI_ERRORS_PHONE, {}, 'validators'), () => {
        const phoneRegex = /^(\+[0-9]{1,4}|00[0-9]{1,4}|0)[1-9][0-9\s-]{3,}$/
        enforce(value).matches(phoneRegex)
      })
    }

    if (input.type === 'date' && value !== '') {
      test(name, trans(PHANTASIALAND_UI_ERRORS_NOT_OLD_ENOUGH, {}, 'validators'), () => {
        const comparisonDate = new Date()
        comparisonDate.setFullYear(comparisonDate.getFullYear() - 18)
        comparisonDate.setHours(0, 0, 0, 0)
        const dateString = comparisonDate.getFullYear() + '-' + (comparisonDate.getMonth() + 1) + '-' + comparisonDate.getDate()

        enforce(value).isBefore(dateString)
      })
    }

    if (input.hasAttribute('data-is-equal')) {
      const targetName = input.getAttribute('data-is-equal')
      const targetValue = data[targetName]
      test(name, trans(PHANTASIALAND_UI_ERRORS_IS_EQUAL, {}, 'validators'), () => {
        enforce(value).equals(targetValue)
      })
    }

    if (input.hasAttribute('data-text-only') && value !== '') {
      test(name, trans(PHANTASIALAND_UI_ERRORS_TEXT_ONLY, {}, 'validators'), () => {
        const textRegex = /^[\p{L}\p{N}\s\-.,:/!?]+$/u
        enforce(value).matches(textRegex)
      })
    }

    if (input.hasAttribute('data-not-numeric-only') && value !== '') {
      test(name, trans(PHANTASIALAND_UI_ERRORS_NOT_NUMERIC_ONLY, {}, 'validators'), () => {
        const notNumericOnlyRegex = /[^\s\d]/u
        enforce(value).matches(notNumericOnlyRegex)
      })
    }

    const requiredRadioFieldsets = Array.from(form.querySelectorAll('[data-required-fieldset]'))
      .filter(fieldset => fieldset.querySelector('input[type="radio"][required]'))

    requiredRadioFieldsets.forEach(fieldset => {
      const radios = fieldset.querySelectorAll('input[type="radio"]')
      const name = radios[0]?.name
      if (name) {
        test(name, trans(PHANTASIALAND_UI_ERRORS_FIELDSET_REQUIRED, {}, 'validators'), () => {
          const checked = Array.from(radios).some(radio => radio.checked)
          enforce(checked).isTruthy()
        })
      }
    })
  }, input => input.name)
})

export function validateAndShowErrors (form) {
  const formData = Object.fromEntries(new FormData(form).entries())
  const inputs = Array.from(form.querySelectorAll('input, textarea, select'))

  const result = validationSuite(form)(formData)
  inputs.forEach(input => clearFieldErrorState(form, input))

  const errors = result.getErrors()
  if (Object.keys(errors).length > 0) {
    Object.keys(errors).forEach(field => {
      const input = form.querySelector(`[name="${field}"]`)
      if (input) updateFieldErrorState(form, input, result)
    })

    const firstInputWithError = inputs.find(input => errors[input.name])
    if (firstInputWithError) {
      firstInputWithError.scrollIntoView({ block: 'center' })
      firstInputWithError.focus()
    }
  }

  return errors
}

document.addEventListener('validateAndShowErrors', function (event) {
  event.result = validateAndShowErrors(event.form)
})

function updateFieldErrorState (form, input, suite) {
  const name = input.name
  const messages = suite.getErrors()[name] || []

  if (messages.length > 0) {
    showError(form, input, messages[0])
  } else {
    clearFieldErrorState(form, input)
  }
}

// Die Checkbox-Optik hängt am Label direkt hinter dem Input (.form__checkbox + .form__label),
// die Meldung darf deshalb erst hinter dem Label stehen, wie beim serverseitigen Fehler.
function getErrorAnchor (input) {
  const next = input.nextElementSibling
  if (input.type === 'checkbox' && next !== null && next.tagName.toLowerCase() === 'label') {
    return next
  }

  return input
}

function clearFieldErrorState (form, input) {
  input.classList.remove(formErrorClass)
  input.removeAttribute('aria-describedby')

  let formError = getErrorAnchor(input).nextElementSibling

  if (input.type === 'radio') {
    const fieldset = input.closest('[data-required-fieldset]')
    if (fieldset) {
      fieldset.classList.remove(formErrorClass)
      formError = fieldset.nextElementSibling
    }
  }

  if (input.tagName.toLowerCase() === 'select' && input.closest(formSelectWrapperClass)) {
    const wrapper = input.closest(formSelectWrapperClass)
    wrapper.classList.remove(formErrorClass)
    formError = wrapper.nextElementSibling
  }

  if (formError && formError.hasAttribute(formErrorDataAttribute)) {
    formError.remove()
  }
}

function getFieldLabel (input) {
  if (input.hasAttribute('data-vest-label')) {
    return input.getAttribute('data-vest-label')
  }

  let labelText = ''

  if (input.id) {
    const label = document.querySelector(`label[for="${input.id}"]`)
    if (label) {
      labelText = label.textContent
    }
  }

  if (!labelText) {
    labelText = input.name
  }

  return labelText.replace(/[*:\s]+$/, '').trim()
}

function showError (form, input, message) {
  clearFieldErrorState(form, input)

  const template = document.getElementById('error-message-template')
  const errorNode = template.content.cloneNode(true)
  const errorContainer = errorNode.querySelector('[' + formErrorDataAttribute + ']')
  const errorId = `error-${input.name}`

  if (errorContainer) {
    errorContainer.id = errorId
    errorContainer.querySelector('[data-js-form-error-message]').textContent = message
  }

  if (input.type !== 'radio') {
    if (input.tagName.toLowerCase() === 'select' && input.closest(formSelectWrapperClass)) {
      const wrapper = input.closest(formSelectWrapperClass)
      wrapper.insertAdjacentElement('afterend', errorContainer)
      wrapper.classList.add(formErrorClass)
    } else {
      getErrorAnchor(input).insertAdjacentElement('afterend', errorContainer)
      input.classList.add(formErrorClass)
    }
  } else {
    const fieldset = input.closest('[data-required-fieldset]')
    if (fieldset && fieldset.querySelector('input[type="radio"][required]')) {
      fieldset.insertAdjacentElement('afterend', errorContainer)
      fieldset.classList.add(formErrorClass)
    }
  }

  input.setAttribute('aria-describedby', errorId)
}
